import React, { useState } from 'react';
import {
  Box, Container, Paper, Typography, TextField, Button, Stepper, Step, StepLabel,
  Alert, CircularProgress, IconButton, Tooltip, Snackbar, Card, CardContent
} from '@mui/material';
import {
  Business, Person, Phone, Email, Lock, ContentCopy, CheckCircle,
  Computer, ArrowForward, ArrowBack
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const getApiUrl = (): string => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { protocol, hostname, port } = window.location;
  if (port === '3000') return `${protocol}//${hostname}:8080/api`;
  return `${protocol}//${hostname}${port ? ':' + port : ''}/api`;
};
const API_URL = getApiUrl();

const Setup: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [agentToken, setAgentToken] = useState('');

  const [form, setForm] = useState({
    clinicName: '',
    contactName: '',
    phone: '',
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const steps = [
    t('setup.stepInfo'),
    t('setup.stepInstall'),
    t('setup.stepDone')
  ];

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.clinicName.trim()) newErrors.clinicName = t('setup.clinicRequired');
    if (!form.contactName.trim()) newErrors.contactName = t('setup.contactRequired');
    if (!form.phone.trim()) newErrors.phone = t('setup.phoneRequired');
    if (!form.email.trim()) newErrors.email = t('setup.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = t('setup.emailInvalid');
    if (!form.password) newErrors.password = t('setup.passwordRequired');
    else if (form.password.length < 6) newErrors.password = t('setup.passwordMin');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validate()) return;

    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/setup/register`, form);
      setAgentToken(res.data.agentToken);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || t('setup.registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getScript = () => {
    const { protocol, hostname, port } = window.location;
    const backendUrl = port === '3000'
      ? `${protocol}//${hostname}:8080`
      : `${protocol}//${hostname}${port ? ':' + port : ''}`;
    const hbUrl = backendUrl + '/api/agent/heartbeat';
    const crUrl = backendUrl + '/api/agent/command-result';
    const lines = [
      '$token = "' + agentToken + '"',
      '$scriptContent = @"',
      '$AgentToken = "' + agentToken + '"',
      '$HeartbeatURL = "' + hbUrl + '"',
      '$CommandResultURL = "' + crUrl + '"',
      '$LastIP = ""',
      '',
      'function Execute-Command($cmd) {',
      '  $type = $cmd.type; $payload = $cmd.payload | ConvertFrom-Json; $result = ""; $success = $true',
      '  try {',
      '    switch ($type) {',
      '      "CREATE_USER" {',
      '        $sp = ConvertTo-SecureString $payload.windowsPassword -AsPlainText -Force',
      '        New-LocalUser -Name $payload.windowsUsername -Password $sp -FullName $payload.windowsUsername -Description "DenTech RDP User" -ErrorAction Stop',
      '        Add-LocalGroupMember -Group "Remote Desktop Users" -Member $payload.windowsUsername -ErrorAction Stop',
      '        Add-LocalGroupMember -Group "Users" -Member $payload.windowsUsername -ErrorAction SilentlyContinue',
      '        $result = "User created: $($payload.windowsUsername)"',
      '      }',
      '      "DELETE_USER" {',
      '        Remove-LocalUser -Name $payload.windowsUsername -ErrorAction Stop',
      '        $result = "User deleted: $($payload.windowsUsername)"',
      '      }',
      '      "ENABLE_USER" { Enable-LocalUser -Name $payload.windowsUsername -ErrorAction Stop; $result = "User enabled" }',
      '      "DISABLE_USER" { Disable-LocalUser -Name $payload.windowsUsername -ErrorAction Stop; $result = "User disabled" }',
      '      default { $result = "Unknown: $type"; $success = $false }',
      '    }',
      '  } catch { $result = $_.Exception.Message; $success = $false }',
      '  $body = @{ agentToken=$AgentToken; commandId=$cmd.id; status=$(if($success){"COMPLETED"}else{"FAILED"}); result=$result } | ConvertTo-Json',
      '  try { Invoke-RestMethod -Uri $CommandResultURL -Method POST -Body $body -ContentType "application/json" -TimeoutSec 10 } catch {}',
      '}',
      '',
      'while ($true) {',
      '  try {',
      '    $ip = (Invoke-RestMethod -Uri "https://api.ipify.org" -TimeoutSec 10)',
      '    $body = @{ agentToken=$AgentToken; ipAddress=$ip; computerName=$env:COMPUTERNAME } | ConvertTo-Json',
      '    $res = Invoke-RestMethod -Uri $HeartbeatURL -Method POST -Body $body -ContentType "application/json" -TimeoutSec 10',
      '    if ($ip -ne $LastIP) { $LastIP = $ip }',
      '    if ($res.commands) { foreach ($cmd in $res.commands) { Execute-Command $cmd } }',
      '  } catch {}',
      '  Start-Sleep -Seconds 30',
      '}',
      '"@',
      'Set-Content -Path "C:\\DenTech-Agent.ps1" -Value $scriptContent -Encoding UTF8',
      '$action = New-ScheduledTaskAction -Execute "powershell" -Argument "-WindowStyle Hidden -ExecutionPolicy Bypass -File C:\\DenTech-Agent.ps1"',
      '$trigger = New-ScheduledTaskTrigger -AtStartup',
      '$settings = New-ScheduledTaskSettingsSet -AllowStartIfOnBatteries -DontStopIfGoingOnBatteries -StartWhenAvailable',
      'Register-ScheduledTask -TaskName "DenTech-Agent" -Action $action -Trigger $trigger -Settings $settings -RunLevel Highest -Force',
      'Start-ScheduledTask -TaskName "DenTech-Agent"',
      'Write-Host "DenTech Agent installed successfully!" -ForegroundColor Green',
    ];
    return lines.join('\n');
  };

  const getInstallerUrl = () => `${API_URL}/setup/agent-installer/${encodeURIComponent(agentToken)}`;

  const handleDownloadInstaller = () => {
    if (!agentToken) return;
    const a = document.createElement('a');
    a.href = getInstallerUrl();
    a.download = 'Install-CloudRDP-Agent.bat';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getScript());
    setCopied(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%)', py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Computer sx={{ fontSize: 60, color: '#fff', mb: 1 }} />
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
            DenTech
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {t('setup.title')}
          </Typography>
        </Box>

        {/* Stepper */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Step Content */}
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          {/* Step 0: Client Info */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
                {t('setup.fillInfo')}
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={t('setup.clinicName')}
                  value={form.clinicName}
                  onChange={e => setForm({ ...form, clinicName: e.target.value })}
                  error={!!errors.clinicName}
                  helperText={errors.clinicName}
                  fullWidth
                  InputProps={{ startAdornment: <Business sx={{ mr: 1, color: 'action.active' }} /> }}
                />
                <TextField
                  label={t('setup.contactName')}
                  value={form.contactName}
                  onChange={e => setForm({ ...form, contactName: e.target.value })}
                  error={!!errors.contactName}
                  helperText={errors.contactName}
                  fullWidth
                  InputProps={{ startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} /> }}
                />
                <TextField
                  label={t('setup.phone')}
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  fullWidth
                  InputProps={{ startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} /> }}
                />
                <TextField
                  label={t('setup.email')}
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  error={!!errors.email}
                  helperText={errors.email}
                  fullWidth
                  InputProps={{ startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} /> }}
                />
                <TextField
                  label={t('setup.password')}
                  type="password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  error={!!errors.password}
                  helperText={errors.password || t('setup.passwordHint')}
                  fullWidth
                  InputProps={{ startAdornment: <Lock sx={{ mr: 1, color: 'action.active' }} /> }}
                />

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleRegister}
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : (isRtl ? <ArrowBack /> : <ArrowForward />)}
                  sx={{ mt: 2, py: 1.5, fontSize: '1.1rem', fontWeight: 700, borderRadius: 2 }}
                >
                  {loading ? t('setup.registering') : t('setup.next')}
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 1: Install Agent */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                {t('setup.installTitle')}
              </Typography>
              <Typography sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
                {t('setup.installDesc')}
              </Typography>

              <Alert severity="info" sx={{ mb: 3 }}>
                {t('setup.installNote')}
              </Alert>

              <Alert severity="success" sx={{ mb: 3 }}>
                تحميل ملف واحد وتشغيله كمسؤول يكمل كل شيء تلقائيًا (بدون نسخ PowerShell يدويًا).
              </Alert>

              <Card variant="outlined" sx={{ mb: 3, bgcolor: '#1a1a2e', borderRadius: 2 }}>
                <CardContent sx={{ position: 'relative' }}>
                  <Tooltip title={t('setup.copyCommand')}>
                    <IconButton
                      onClick={handleCopy}
                      sx={{ position: 'absolute', top: 8, [isRtl ? 'left' : 'right']: 8, color: '#4fc3f7' }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                  <Typography
                    component="pre"
                    sx={{
                      color: '#4fc3f7', fontFamily: 'monospace', fontSize: '0.8rem',
                      overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                      direction: 'ltr', textAlign: 'left', pr: 5
                    }}
                  >
                    {getScript()}
                  </Typography>
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleDownloadInstaller}
                  sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 700, borderRadius: 2 }}
                >
                  تنزيل ملف التثبيت التلقائي
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleCopy}
                  startIcon={<ContentCopy />}
                  sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 700, borderRadius: 2 }}
                >
                  نسخ السكربت (احتياطي)
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setActiveStep(2)}
                  sx={{ py: 1.5, minWidth: 150, borderRadius: 2 }}
                >
                  {t('setup.done')}
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 2: Done */}
          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                {t('setup.successTitle')}
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
                {t('setup.successDesc')}
              </Typography>
              <Alert severity="success" sx={{ maxWidth: 500, mx: 'auto' }}>
                {t('setup.successNote')}
              </Alert>
            </Box>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={t('setup.copied')}
      />
    </Box>
  );
};

export default Setup;
